import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Activity, AlertTriangle, CalendarClock, CheckCircle2, ChevronRight,
  Clock, Inbox, Loader2, ShieldAlert, ShieldCheck, Stethoscope,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FLAG_OPTIONS = [
  { v: 'vitals_unstable',   l: 'Vitals unstable' },
  { v: 'med_nonadherence',  l: 'Medication non-adherence' },
  { v: 'weight_gain_2kg',   l: 'Weight gain ≥ 2kg' },
  { v: 'new_symptom',       l: 'New symptom' },
  { v: 'ed_visit',          l: 'ED visit' },
  { v: 'caregiver_burnout', l: 'Caregiver burnout' },
  { v: 'other',             l: 'Other concern' },
];

const TIER_TONE = {
  low:       'bg-emerald-100 text-emerald-800',
  moderate:  'bg-amber-100 text-amber-800',
  high:      'bg-orange-100 text-orange-900',
  very_high: 'bg-red-100 text-red-900',
};

const KIND_LABEL = {
  h_48:   '48-hour check-in',
  h_72:   '72-hour check-in',
  day_7:  'Day-7 check-in',
  day_14: 'Day-14 check-in',
  day_21: 'Day-21 check-in',
};

const Section = ({ title, items, tone, onComplete, busyId }) => (
  <section className="cn-card rounded-2xl p-5" data-testid={`spine-tasks-section-${title.toLowerCase()}`}>
    <div className="flex items-center justify-between mb-3">
      <h3 className="cn-display text-lg text-cn-text inline-flex items-center gap-2">
        {title}
      </h3>
      <span className={`text-xs font-bold px-2 py-1 rounded-full ${tone}`} data-testid={`spine-tasks-count-${title.toLowerCase()}`}>
        {items.length}
      </span>
    </div>
    {items.length === 0 ? (
      <div className="text-sm text-cn-text-muted py-3">Nothing here.</div>
    ) : (
      <div className="space-y-2">
        {items.map((t) => (
          <div
            key={t.touchpoint_id}
            className="rounded-xl border border-cn-border px-3 py-2 flex items-center justify-between gap-2 hover:bg-cn-soft/30"
            data-testid={`spine-task-row-${t.touchpoint_id}`}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${TIER_TONE[t.tier] || 'bg-slate-100 text-slate-700'}`}>
                  {t.tier}
                </span>
                <span className="text-sm font-semibold text-cn-text">{KIND_LABEL[t.kind] || t.kind}</span>
              </div>
              <div className="text-xs text-cn-text-muted">
                Patient #{(t.patient_id || '').slice(0, 8)} · due {new Date(t.due_at).toLocaleString()}
              </div>
            </div>
            <button
              onClick={() => onComplete(t)}
              disabled={busyId === t.touchpoint_id}
              className="cn-btn cn-btn-primary !py-1.5 !px-3 text-xs"
              data-testid={`spine-task-complete-btn-${t.touchpoint_id}`}
            >
              {busyId === t.touchpoint_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <>Complete <ChevronRight className="w-3.5 h-3.5" /></>}
            </button>
          </div>
        ))}
      </div>
    )}
  </section>
);

const CompleteTouchpointModal = ({ open, onClose, onConfirm, task, busy }) => {
  const [flags, setFlags] = useState([]);
  const [notes, setNotes] = useState('');
  useEffect(() => { if (open) { setFlags([]); setNotes(''); } }, [open]);
  const toggle = (v) => setFlags((cur) => cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="spine-complete-touchpoint-modal">
        <DialogHeader>
          <DialogTitle>Complete touchpoint</DialogTitle>
          <DialogDescription>
            {task && (<>
              <span className="font-semibold">{KIND_LABEL[task.kind] || task.kind}</span> ·
              patient #{(task?.patient_id || '').slice(0, 8)}. Flag any decompensation signs
              — they fire an alert to Ops + the planner.
            </>)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <div className="text-xs font-semibold text-cn-text-muted mb-2">Decompensation flags (optional)</div>
            <div className="flex flex-wrap gap-2" data-testid="spine-decomp-flags">
              {FLAG_OPTIONS.map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => toggle(o.v)}
                  data-testid={`spine-decomp-flag-${o.v}`}
                  className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition-colors ${flags.includes(o.v) ? 'bg-red-600 text-white border-red-600' : 'bg-cn-bg text-cn-text border-cn-border hover:border-red-300'}`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-cn-text-muted mb-1">Notes</div>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Observations, plan, escalation steps…" className="rounded-xl" data-testid="spine-complete-notes" />
          </div>
          {flags.length > 0 && (
            <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800 flex items-center gap-2" data-testid="spine-complete-alert-warning">
              <ShieldAlert className="w-4 h-4" /> Completing with {flags.length} flag{flags.length > 1 ? 's' : ''} will raise an alert.
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <button onClick={onClose} className="cn-btn flex-1 justify-center" data-testid="spine-complete-cancel">Cancel</button>
            <button
              disabled={busy}
              onClick={() => onConfirm(task, flags, notes)}
              className="cn-btn cn-btn-primary flex-1 justify-center"
              data-testid="spine-complete-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Complete'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function SpineClinicianWatches() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState({ overdue: [], today: [], upcoming: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/spine/clinician/watch-tasks`, getAuthHeader());
      setData(res.data || { overdue: [], today: [], upcoming: [], total: 0 });
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 402) {
        toast.error(`Entitlement disabled: ${detail?.entitlement_required || 'readmission_watch'}`);
      } else {
        toast.error('Failed to load watch tasks');
      }
    } finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const confirmComplete = async (t, flags, notes) => {
    setBusy(t.touchpoint_id);
    try {
      const res = await axios.post(
        `${API}/spine/watches/${t.watch_id}/touchpoints/${t.touchpoint_id}/complete`,
        { decompensation_flags: flags, notes: notes || null },
        getAuthHeader(),
      );
      if (res.data?.alert_id) {
        toast.success('Touchpoint complete — alert raised to Ops + planner');
      } else {
        toast.success('Touchpoint complete');
      }
      setTarget(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not complete touchpoint');
    } finally { setBusy(null); }
  };

  return (
    <DashboardShell
      title="Readmission watch"
      subtitle="30-day post-discharge touchpoints assigned to you. Catch decompensation before it becomes a readmission."
      navItems={[]}
      testId="spine-clinician-watches"
    >
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="cn-card rounded-2xl p-5" data-testid="spine-watch-kpi-overdue">
          <div className="w-10 h-10 rounded-xl bg-red-100 text-red-700 flex items-center justify-center mb-3">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="text-3xl font-display font-semibold text-cn-text">{data.overdue.length}</div>
          <div className="text-sm text-cn-text-muted mt-0.5">Overdue</div>
        </div>
        <div className="cn-card rounded-2xl p-5" data-testid="spine-watch-kpi-today">
          <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center mb-3">
            <Clock className="w-5 h-5" />
          </div>
          <div className="text-3xl font-display font-semibold text-cn-text">{data.today.length}</div>
          <div className="text-sm text-cn-text-muted mt-0.5">Due today</div>
        </div>
        <div className="cn-card rounded-2xl p-5" data-testid="spine-watch-kpi-upcoming">
          <div className="w-10 h-10 rounded-xl bg-cn-soft text-cn-primary flex items-center justify-center mb-3">
            <CalendarClock className="w-5 h-5" />
          </div>
          <div className="text-3xl font-display font-semibold text-cn-text">{data.upcoming.length}</div>
          <div className="text-sm text-cn-text-muted mt-0.5">Upcoming</div>
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-cn-primary" /></div>
      ) : data.total === 0 ? (
        <div className="cn-card rounded-3xl p-10 text-center" data-testid="spine-watch-empty">
          <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-7 h-7 text-emerald-700" />
          </div>
          <h3 className="cn-display text-2xl text-cn-text mb-2">All clear</h3>
          <p className="text-cn-text-muted max-w-md mx-auto">
            No pending readmission-watch touchpoints. Watches auto-spawn when you accept a placement.
          </p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-4" data-testid="spine-watch-sections">
          <Section title="Overdue" items={data.overdue} tone="bg-red-100 text-red-800" onComplete={setTarget} busyId={busy} />
          <Section title="Today" items={data.today} tone="bg-amber-100 text-amber-800" onComplete={setTarget} busyId={busy} />
          <Section title="Upcoming" items={data.upcoming} tone="bg-cn-soft text-cn-primary" onComplete={setTarget} busyId={busy} />
        </div>
      )}

      <CompleteTouchpointModal
        open={!!target}
        onClose={() => setTarget(null)}
        onConfirm={confirmComplete}
        task={target}
        busy={busy === target?.touchpoint_id}
      />
    </DashboardShell>
  );
}
